<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[1050] flex items-center justify-center p-2.5 sm:p-4 bg-slate-900/60 backdrop-blur-sm"
  >
    <div
      class="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[92vh] sm:max-h-[88vh]"
    >
      <!-- Modal Header -->
      <div
        class="px-4 sm:px-6 py-3.5 sm:py-4 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white flex items-center justify-between flex-shrink-0 gap-2"
      >
        <div class="flex items-center space-x-2.5 sm:space-x-3 min-w-0">
          <div
            class="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 flex-shrink-0"
          >
            <Wallet class="w-4 h-4 sm:w-5 sm:h-5 text-indigo-400" />
          </div>
          <div class="min-w-0 truncate">
            <h3 class="text-sm sm:text-lg font-bold truncate">Student Fee Account</h3>
            <p class="text-[11px] sm:text-xs text-indigo-200 font-medium truncate">
              Complete fee ledger & receipts
            </p>
          </div>
        </div>
        <div class="flex items-center space-x-1.5 sm:space-x-2 flex-shrink-0">
          <button
            @click="$emit('configure', student)"
            class="px-2.5 sm:px-3 py-1.5 text-xs font-semibold bg-white/10 hover:bg-white/20 text-white rounded-lg border border-white/20 transition flex items-center gap-1.5"
          >
            <Sliders class="w-3.5 h-3.5" />
            <span class="hidden sm:inline">Configure Fees</span>
            <span class="inline sm:hidden">Configure</span>
          </button>
          <button
            @click="$emit('close')"
            class="p-1.5 sm:p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition"
          >
            <X class="w-5 h-5" />
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="p-12 text-center flex-1 flex flex-col items-center justify-center">
        <AppLoader
          variant="card"
          size="md"
          message="Loading Student Fee Ledger..."
          subtext="Calculating payments, dues, and transaction history"
          color="indigo"
        />
      </div>

      <!-- Content Area -->
      <div v-else-if="student" class="flex-1 overflow-y-auto overscroll-contain p-3.5 sm:p-6 space-y-4 sm:space-y-6">
        <!-- Student Profile Card -->
        <div class="p-3.5 sm:p-4 bg-gradient-to-r from-indigo-50/70 via-purple-50/50 to-slate-50 rounded-2xl border border-indigo-100/80 flex flex-col sm:flex-row items-center sm:items-start justify-between gap-4">
          <div class="flex items-center space-x-3 sm:space-x-4">
            <div class="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white shadow-md border border-indigo-100 flex items-center justify-center overflow-hidden flex-shrink-0">
              <img
                v-if="student.photo"
                :src="student.photo"
                alt="Student Photo"
                class="w-full h-full object-cover"
              />
              <User v-else class="w-7 h-7 text-indigo-600" />
            </div>
            <div>
              <div class="flex items-center gap-2">
                <h4 class="text-base sm:text-lg font-bold text-slate-900">{{ student.name }}</h4>
                <span
                  class="px-2 py-0.5 text-[10px] font-bold uppercase rounded-md"
                  :class="
                    student.status === 'active'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-slate-200 text-slate-700'
                  "
                >
                  {{ student.status || "active" }}
                </span>
              </div>
              <p class="text-xs text-slate-600 font-medium mt-0.5">
                Class: <span class="font-bold text-slate-800">{{ student.class?.name || "N/A" }}</span>
                <span v-if="student.class?.section"> ({{ student.class.section }})</span>
                • Roll: <span class="font-bold text-slate-800">{{ student.rollNumber || "N/A" }}</span>
                <span v-if="student.attendanceID"> • ID: {{ student.attendanceID }}</span>
              </p>
              <p class="text-xs text-slate-500">
                Session: <span class="font-semibold text-indigo-700">{{ feeStructure?.academicSession || student.session || "Current" }}</span>
                <span v-if="student.phone"> • Phone: {{ student.phone }}</span>
              </p>
            </div>
          </div>

          <!-- Quick Pay Button -->
          <div class="flex sm:flex-col gap-2 w-full sm:w-auto">
            <button
              @click="$emit('record-payment', { student, feeStructure, monthlyRecords })"
              class="flex-1 sm:flex-initial px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold rounded-xl shadow-md shadow-emerald-600/20 transition flex items-center justify-center gap-1.5"
            >
              <PlusCircle class="w-4 h-4" />
              Record Payment
            </button>
          </div>
        </div>

        <!-- Two Summary Cards: Admission & Monthly -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Card 1: Admission Fee Summary -->
          <div class="p-5 bg-white rounded-2xl border border-slate-200 shadow-sm space-y-3">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <div class="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600">
                  <GraduationCap class="w-4 h-4" />
                </div>
                <div>
                  <h5 class="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Admission Fee
                  </h5>
                  <span class="text-[11px] text-slate-400">One-time enrollment charge</span>
                </div>
              </div>
              <span
                class="px-2.5 py-1 rounded-full text-xs font-bold uppercase"
                :class="
                  feeStructure?.admissionFee?.status === 'paid'
                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                    : feeStructure?.admissionFee?.status === 'partial'
                    ? 'bg-amber-100 text-amber-800 border border-amber-200'
                    : 'bg-red-100 text-red-800 border border-red-200'
                "
              >
                {{ feeStructure?.admissionFee?.status || "Unpaid" }}
              </span>
            </div>

            <div class="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100 text-center">
              <div>
                <span class="text-[10px] text-slate-400 font-semibold uppercase block">Total Due</span>
                <span class="text-sm font-bold text-slate-800 font-mono">
                  ₹{{ (feeStructure?.admissionFee?.amount || 0).toLocaleString("en-IN") }}
                </span>
              </div>
              <div>
                <span class="text-[10px] text-slate-400 font-semibold uppercase block">Amount Paid</span>
                <span class="text-sm font-bold text-emerald-600 font-mono">
                  ₹{{ (feeStructure?.admissionFee?.paidAmount || 0).toLocaleString("en-IN") }}
                </span>
              </div>
              <div>
                <span class="text-[10px] text-slate-400 font-semibold uppercase block">Remaining</span>
                <span class="text-sm font-bold text-red-600 font-mono">
                  ₹{{ (feeStructure?.admissionFee?.balance || 0).toLocaleString("en-IN") }}
                </span>
              </div>
            </div>

            <div v-if="feeStructure?.admissionFee?.balance > 0" class="pt-2">
              <button
                @click="$emit('record-payment', { student, feeStructure, monthlyRecords, category: 'admission' })"
                class="w-full py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-bold rounded-lg transition border border-indigo-200"
              >
                Pay Admission Balance (₹{{ feeStructure.admissionFee.balance.toLocaleString("en-IN") }})
              </button>
            </div>
          </div>

          <!-- Card 2: Monthly Fee Summary -->
          <div class="p-5 bg-white rounded-2xl border border-slate-200 shadow-sm space-y-3">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <div class="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center text-purple-600">
                  <Calendar class="w-4 h-4" />
                </div>
                <div>
                  <h5 class="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Monthly Tuition Fee
                  </h5>
                  <span class="text-[11px] text-slate-400">
                    Rate: ₹{{ (feeStructure?.monthlyFee?.amount || 0).toLocaleString("en-IN") }}/month
                  </span>
                </div>
              </div>
              <span class="text-xs font-semibold text-slate-500">
                Due: {{ feeStructure?.monthlyFee?.dueDay || 10 }}th of month
              </span>
            </div>

            <div class="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100 text-center">
              <div>
                <span class="text-[10px] text-slate-400 font-semibold uppercase block">Period Due</span>
                <span class="text-sm font-bold text-slate-800 font-mono">
                  ₹{{ (monthlySummary?.totalMonthlyDue || 0).toLocaleString("en-IN") }}
                </span>
              </div>
              <div>
                <span class="text-[10px] text-slate-400 font-semibold uppercase block">Total Paid</span>
                <span class="text-sm font-bold text-emerald-600 font-mono">
                  ₹{{ (monthlySummary?.totalMonthlyPaid || 0).toLocaleString("en-IN") }}
                </span>
              </div>
              <div>
                <span class="text-[10px] text-slate-400 font-semibold uppercase block">Outstanding</span>
                <span class="text-sm font-bold text-red-600 font-mono">
                  ₹{{ (monthlySummary?.totalMonthlyOutstanding || 0).toLocaleString("en-IN") }}
                </span>
              </div>
            </div>

            <div class="pt-2 text-center text-xs text-slate-500">
              Active since {{ getMonthName(feeStructure?.monthlyFee?.startMonth) }} {{ feeStructure?.monthlyFee?.startYear }}
            </div>
          </div>
        </div>

        <!-- Monthly Fee Obligations Table -->
        <div class="space-y-3">
          <div class="flex items-center justify-between">
            <h5 class="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
              <CalendarDays class="w-4 h-4 text-purple-600" />
              Monthly Billing Ledger
            </h5>
            <span class="text-xs text-slate-500">
              {{ monthlyRecords.length }} billing cycle{{ monthlyRecords.length > 1 ? "s" : "" }}
            </span>
          </div>

          <div class="border border-slate-200 rounded-2xl overflow-hidden bg-white shadow-sm">
            <div class="overflow-x-auto">
              <table class="w-full text-left text-xs min-w-[620px]">
                <thead class="bg-slate-50 text-slate-600 uppercase font-bold text-[11px] border-b border-slate-200">
                  <tr>
                    <th class="py-3 px-4">Billing Month</th>
                    <th class="py-3 px-4">Due Date</th>
                    <th class="py-3 px-4 text-right">Fee Due</th>
                    <th class="py-3 px-4 text-right">Amount Paid</th>
                    <th class="py-3 px-4 text-right">Balance</th>
                    <th class="py-3 px-4 text-center">Status</th>
                    <th class="py-3 px-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 font-medium">
                  <tr
                    v-for="rec in monthlyRecords"
                    :key="rec._id"
                    :class="rec.status === 'overdue' ? 'bg-red-50/30' : 'hover:bg-slate-50/70'"
                  >
                    <td class="py-3 px-4 font-bold text-slate-800">
                      {{ rec.monthName }} {{ rec.year }}
                    </td>
                    <td class="py-3 px-4 text-slate-600">
                      {{ formatDateShort(rec.dueDate) }}
                    </td>
                    <td class="py-3 px-4 text-right font-mono font-semibold text-slate-800">
                      ₹{{ rec.feeDue.toLocaleString("en-IN") }}
                    </td>
                    <td class="py-3 px-4 text-right font-mono font-semibold text-emerald-600">
                      ₹{{ rec.amountPaid.toLocaleString("en-IN") }}
                    </td>
                    <td class="py-3 px-4 text-right font-mono font-bold" :class="rec.balance > 0 ? 'text-red-600' : 'text-slate-400'">
                      ₹{{ rec.balance.toLocaleString("en-IN") }}
                    </td>
                    <td class="py-3 px-4 text-center">
                      <span
                        class="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase inline-block"
                        :class="
                          rec.status === 'paid'
                            ? 'bg-emerald-100 text-emerald-800'
                            : rec.status === 'partial'
                            ? 'bg-amber-100 text-amber-800'
                            : rec.status === 'overdue'
                            ? 'bg-red-100 text-red-800 font-black'
                            : 'bg-blue-100 text-blue-800'
                        "
                      >
                        {{ rec.status }}
                      </span>
                    </td>
                    <td class="py-3 px-4 text-right">
                      <button
                        v-if="rec.balance > 0"
                        @click="$emit('record-payment', { student, feeStructure, monthlyRecords, category: 'monthly', monthlyFeeRecordId: rec._id })"
                        class="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition shadow-sm"
                      >
                        {{ rec.amountPaid > 0 ? "Pay Balance" : "Record Payment" }}
                      </button>
                      <button
                        v-else
                        @click="viewMonthlyReceipt(rec)"
                        class="px-2.5 py-1 text-slate-600 hover:text-indigo-600 text-xs font-semibold hover:bg-indigo-50 rounded-lg transition"
                      >
                        Receipt
                      </button>
                    </td>
                  </tr>

                  <tr v-if="monthlyRecords.length === 0">
                    <td colspan="7" class="py-8 text-center text-slate-400">
                      No monthly billing records yet. Configure student monthly fees to generate obligations.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Payment Transactions History -->
        <div class="space-y-3">
          <div class="flex items-center justify-between">
            <h5 class="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
              <Receipt class="w-4 h-4 text-indigo-600" />
              Payment Transactions & Receipts
            </h5>
            <span class="text-xs text-slate-500">
              {{ transactions.length }} transaction{{ transactions.length > 1 ? "s" : "" }}
            </span>
          </div>

          <div class="border border-slate-200 rounded-2xl overflow-hidden bg-white shadow-sm">
            <div class="overflow-x-auto">
              <table class="w-full text-left text-xs min-w-[700px]">
                <thead class="bg-slate-50 text-slate-600 uppercase font-bold text-[11px] border-b border-slate-200">
                  <tr>
                    <th class="py-3 px-4">Receipt No</th>
                    <th class="py-3 px-4">Date</th>
                    <th class="py-3 px-4">Category</th>
                    <th class="py-3 px-4">Period</th>
                    <th class="py-3 px-4">Method</th>
                    <th class="py-3 px-4 text-right">Amount</th>
                    <th class="py-3 px-4">Recorded By</th>
                    <th class="py-3 px-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 font-medium">
                  <tr v-for="tx in transactions" :key="tx._id" class="hover:bg-slate-50/70">
                    <td class="py-3 px-4 font-mono font-bold text-indigo-700">
                      {{ tx.receiptNumber }}
                    </td>
                    <td class="py-3 px-4 text-slate-600">
                      {{ formatDate(tx.paymentDate) }}
                    </td>
                    <td class="py-3 px-4 capitalize font-semibold text-slate-700">
                      {{ tx.feeCategory === "admission" ? "Admission" : "Monthly" }}
                    </td>
                    <td class="py-3 px-4 text-slate-600">
                      {{ tx.feeCategory === "monthly" && tx.monthName ? `${tx.monthName} ${tx.year}` : "One-Time" }}
                    </td>
                    <td class="py-3 px-4 text-slate-700">
                      {{ tx.paymentMethod }}
                      <span v-if="tx.transactionReference" class="text-[10px] text-slate-400 block font-mono">
                        {{ tx.transactionReference }}
                      </span>
                    </td>
                    <td class="py-3 px-4 text-right font-mono font-bold text-emerald-700 text-sm">
                      ₹{{ tx.amount.toLocaleString("en-IN") }}
                    </td>
                    <td class="py-3 px-4 text-slate-500 text-[11px]">
                      {{ tx.recordedByName || "Admin" }}
                    </td>
                    <td class="py-3 px-4 text-right">
                      <button
                        @click="$emit('view-receipt', tx.receiptNumber)"
                        class="px-2.5 py-1 text-xs font-bold text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition"
                      >
                        View Receipt
                      </button>
                    </td>
                  </tr>

                  <tr v-if="transactions.length === 0">
                    <td colspan="8" class="py-8 text-center text-slate-400">
                      No payments recorded yet for this student.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div
        class="flex-shrink-0 px-4 sm:px-6 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between"
      >
        <span class="text-xs text-slate-500 font-medium hidden sm:inline">
          {{ student?.name }} • {{ student?.class?.name || "N/A" }}
        </span>
        <div class="flex items-center gap-2 w-full sm:w-auto justify-end">
          <button
            @click="$emit('close')"
            class="flex-1 sm:flex-initial px-4 py-2 text-xs font-bold text-slate-700 bg-white hover:bg-slate-100 rounded-xl border border-slate-300 transition"
          >
            Close
          </button>
          <button
            v-if="student"
            @click="$emit('record-payment', { student, feeStructure, monthlyRecords })"
            class="flex-1 sm:flex-initial px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold rounded-xl shadow-md transition flex items-center justify-center gap-1.5"
          >
            <PlusCircle class="w-3.5 h-3.5" />
            <span>Record Payment</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import {
    Wallet,
    X,
    Sliders,
    GraduationCap,
    Calendar,
    CalendarDays,
    Receipt,
    PlusCircle,
    User,
  } from "lucide-vue-next";

  const props = defineProps({
    show: { type: Boolean, default: false },
    student: { type: Object, default: null },
    feeStructure: { type: Object, default: null },
    monthlySummary: { type: Object, default: () => ({}) },
    monthlyRecords: { type: Array, default: () => [] },
    transactions: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
  });

  const emit = defineEmits([
    "close",
    "configure",
    "record-payment",
    "view-receipt",
  ]);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const getMonthName = (m) => (m && months[m - 1] ? months[m - 1] : "N/A");

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const viewMonthlyReceipt = (rec) => {
    // Find latest transaction for this month
    const matchingTx = props.transactions.find(
      (tx) => tx.monthlyFeeRecordId === rec._id || (tx.month === rec.month && tx.year === rec.year)
    );
    if (matchingTx) {
      emit("view-receipt", matchingTx.receiptNumber);
    }
  };
</script>
